import { useRef } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";

interface ReviewObjectsCompleteDialogProps {
  open: boolean;
  /** "plot" when the next image starts the next plot, "image" when it stays in this one. */
  targetScope: "image" | "plot";
  /** Plot the reviewer is finishing; only shown for the "plot" scope. */
  plotId?: string;
  dontAskAgain: boolean;
  onDontAskAgainChange: (value: boolean) => void;
  /** AlertDialog open/close handler (Escape dismisses without advancing). */
  onOpenChange: (open: boolean) => void;
  /** Advance to the next image/plot, saving on the way as normal navigation does. */
  onConfirm: () => void;
  /** Stay on this image so the reviewer can keep working. */
  onCancel: () => void;
}

function describeNextStep(targetScope: "image" | "plot", plotId?: string): string {
  if (targetScope === "image") return "Go to the next image in this plot?";
  if (plotId) return `That was the last image for Plot ${plotId}. Go to the next plot?`;
  return "That was the last image for this plot. Go to the next plot?";
}

/**
 * Shown when a reviewer steps past the last object of an image in review-objects mode. Advancing
 * runs the ordinary navigation path (which auto-saves), so this is a "you're done here" checkpoint
 * rather than a destructive confirmation — hence Enter confirms and the checkbox can retire it for
 * the session.
 */
export function ReviewObjectsCompleteDialog({
  open,
  targetScope,
  plotId,
  dontAskAgain,
  onDontAskAgainChange,
  onOpenChange,
  onConfirm,
  onCancel,
}: ReviewObjectsCompleteDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const isPlot = targetScope === "plot";

  return (
    <AlertDialog
      open={open}
      onOpenChange={onOpenChange}>
      <AlertDialogContent
        onOpenAutoFocus={(event) => {
          // Radix focuses Cancel by default, the right call for a destructive prompt. This one is
          // non-destructive and the reviewer's hand is on the arrow keys, so Enter must advance.
          event.preventDefault();
          confirmRef.current?.focus({ preventScroll: true });
        }}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isPlot ? "You've reviewed all objects in this plot" : "You've reviewed all objects in this image"}
          </AlertDialogTitle>
          <AlertDialogDescription>{describeNextStep(targetScope, plotId)}</AlertDialogDescription>
        </AlertDialogHeader>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
          <Checkbox
            checked={dontAskAgain}
            aria-label="Don't ask again this session"
            onCheckedChange={(checked) => onDontAskAgainChange(checked === true)}
          />
          <span>Don&apos;t ask again this session</span>
        </label>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Keep reviewing</AlertDialogCancel>
          <AlertDialogAction
            ref={confirmRef}
            onClick={onConfirm}>
            {isPlot ? "Go to next plot" : "Go to next image"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
